import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { EmailField } from '@components/email-field';
import { useUnsavedChangesModal } from '@components/generic-modals';
import { IconGear } from '@components/icons/gear';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import {
  forceHideSidebar,
  Sidebar,
  useSidebar,
  useSidebarEventHandlers,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { FieldMappingTextEditor } from '@components/text-editors';
import { ExtendedFieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { useToaster } from '@components/toaster';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyConfig, useSaveJourneyConfig } from './hooks';

import { JourneyCard } from '../components/journey-card';
import { journeyListStrings } from '../components/list-strings';
import { FieldConfigForm } from '../field-config/field-config-form';
import { toMappedEditorValue } from '../field-config/rehydrate-variables';
import {
  areDraftsEqual,
  buildDraftFromFieldConfig,
  buildFieldConfigFromDraft,
  findEmptyBaseFields,
  seedBaseDefaults,
  summarizeDraftCounts,
} from '../field-config/serialize';
import {
  FieldConfigDraft,
  FieldConfigValue,
  JourneyAttribute,
  StoredFieldConfig,
} from '../field-config/types';
import { useBaseFieldDefaults } from '../field-config/use-base-field-defaults';
import { filterVariableGroupsByTypes } from '../field-config/variable-groups';
import { useVariableGroups } from '../helpers';
import { useCompanyTravellerAttributes } from '../hooks';

type JourneyConfigSidebarProps = {
  journeyId: string;
  journeyTitle: string;
  journeyDescription?: string;
};

// Only name + email render in the panel. A stored base.phone is accepted by
// the backend and passed through by the add-to-journey sidebar, but the panel
// no longer exposes an editor for it.
const VISIBLE_BASE_FIELD_KEYS = ['name', 'email'] as const;

const BASE_FIELD_LABEL_KEYS = {
  name: 'fullName',
  email: 'core.email',
} as const;

const BASE_FIELD_ATTRIBUTE_TYPES = {
  name: 'text',
  email: 'email',
} as const;

export const useJourneyConfigSidebar = () => {
  return useSidebar(JourneyConfigSidebar, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });
};

export const JourneyConfigSidebar = ({
  journeyId,
  journeyTitle,
  journeyDescription,
}: JourneyConfigSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const configQuery = useJourneyConfig(journeyId);
  const saveConfig = useSaveJourneyConfig();
  const baseFieldDefaults = useBaseFieldDefaults();
  const showUnsavedChangesModal = useUnsavedChangesModal();
  const { showError } = useToaster();
  const { setOnBeforeChange } = useSidebarEventHandlers();

  const [draft, setDraft] = useState<FieldConfigDraft | null>(null);
  // Protects an in-progress edit from a background refetch (window focus, or
  // another save invalidating this query) — it exists only for that guard,
  // real dirty-tracking is the isDirty comparison below.
  const [hasEdited, setHasEdited] = useState(false);
  const baselineDraftRef = useRef<FieldConfigDraft | null>(null);
  const isSavingRef = useRef(false);

  // Seed the draft once on first successful data load; a background refetch
  // only rebuilds when the draft is still untouched — avoiding silent loss of
  // in-progress edits on window-focus refetches.
  //
  // Seeding: when the stored field_config has an empty `base` (fresh journey,
  // never configured), buildDraftFromFieldConfig leaves the base fields
  // undefined. seedBaseDefaults fills in the name/email defaults so a save
  // without touching them still writes the canonical applicant-variable
  // mappings rather than persisting an empty base. Phone is not seeded — the
  // panel no longer exposes it (an already-stored phone is preserved).
  // The company-traveller catalog is company-wide, so it comes from its own
  // query rather than the per-journey config response. Tagging its entries
  // `source: 'traveller'` is what routes their decisions to the stored
  // `traveller_fields` section; the two catalogs' short_ids are Hashids of
  // different upstream models and can collide.
  const travellerAttributesQuery = useCompanyTravellerAttributes();
  const travellerAttrs = useMemo<JourneyAttribute[]>(
    () =>
      (travellerAttributesQuery.data ?? []).map((attribute) => ({
        ...attribute,
        source: 'traveller' as const,
      })),
    [travellerAttributesQuery.data],
  );

  const configData = configQuery.data;
  useEffect(() => {
    if (!configData) {
      return;
    }
    if (draft !== null && hasEdited) {
      return;
    }
    // Wait for the traveller catalog before seeding: a draft built without it
    // represents no traveller attribute, so the rows would be missing until
    // something else re-rendered the panel. `isFetched` covers the error case
    // too — the panel still opens, and buildFieldConfigFromDraft preserves the
    // stored traveller entries a catalog-less draft cannot show.
    if (!travellerAttributesQuery.isFetched) {
      return;
    }
    const raw = buildDraftFromFieldConfig(configData.field_config, [
      ...configData.attributes,
      ...travellerAttrs,
    ]);
    const seeded = seedBaseDefaults(raw, baseFieldDefaults);
    baselineDraftRef.current = seeded;
    setDraft(seeded);
    setHasEdited(false);
    // Deliberately not depending on `travellerAttrs`: it is a derived array, so
    // its identity changes whenever the query hands back a new `data` reference,
    // and re-running this effect calls setDraft — which re-renders, re-derives,
    // and loops. `isFetched` flipping false -> true is the one transition that
    // has to re-seed.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, travellerAttributesQuery.isFetched]);

  // Real dirty tracking: a draft edited back to exactly what it loaded
  // with is not dirty, unlike the old latched boolean that stayed true for
  // the rest of the sidebar's life once any change fired.
  const isDirty =
    draft !== null &&
    (baselineDraftRef.current === null || !areDraftsEqual(draft, baselineDraftRef.current));

  // Global (company-settings) context: no job, so job/form groups are omitted.
  const variableGroups = useVariableGroups({ jobId: null });
  const fileVariableGroups = useVariableGroups({ jobId: null, types: ['file'] });
  const fileMultiVariableGroups = useVariableGroups({ jobId: null, types: ['file', 'file_multi'] });

  const fieldMappingId = `journey-config-${journeyId}`;

  const attrs = useMemo<JourneyAttribute[]>(
    () => [...(configQuery.data?.attributes ?? []), ...travellerAttrs],
    [configQuery.data?.attributes, travellerAttrs],
  );
  // Base fields are badged "Always required": an empty name or email would
  // create a nameless traveller, and once saved the panel cannot re-seed the
  // default (the key exists). Block the save instead and mark the row.
  const emptyBaseFields = draft ? findEmptyBaseFields(draft, VISIBLE_BASE_FIELD_KEYS) : [];
  const hasEmptyBaseField = emptyBaseFields.length > 0;
  // Summary counts for the journey card: "{fieldsCount} fields · {preFilledCount}
  // pre-filled" — the same format every other surface uses (journey-card.tsx).
  // totalIncluded/mapped already count the base fields as always-shown /
  // always-required, so they map directly onto fieldsCount/preFilledCount.
  const draftCounts = draft
    ? summarizeDraftCounts(draft, VISIBLE_BASE_FIELD_KEYS)
    : { mapped: 0, totalIncluded: 0 };
  // No plural machinery in @locale/translate — branch explicitly on
  // fieldsCount === 1, the same pattern add-fields-modal.tsx uses for its own
  // singular/plural button text.
  const countsText = t(draftCounts.totalIncluded === 1 ? 'fieldsSummaryOne' : 'fieldsSummaryMany', {
    fieldsCount: String(draftCounts.totalIncluded),
    preFilledCount: String(draftCounts.mapped),
  });

  const handleSave = () => {
    // A ref, not saveConfig.isLoading: both `disabled` and isLoading are React
    // state that only settle on a later render, so a fast double-click gets
    // through them and fires the PUT twice. The ref latches synchronously.
    if (!draft || !configQuery.data || isSavingRef.current || hasEmptyBaseField) {
      return;
    }
    // buildFieldConfigFromDraft throws on a malformed variables shape — a
    // programming-error invariant that the editors never produce, but guard it
    // so it surfaces as the standard error toast instead of an uncaught render.
    let fieldConfig: StoredFieldConfig;
    try {
      // The stored config goes in so a section the draft never represented —
      // the traveller catalog is a separate query — is carried over instead of
      // being deleted.
      fieldConfig = buildFieldConfigFromDraft(draft, configQuery.data.field_config);
    } catch {
      showError(t('saveConfigError'));
      return;
    }
    isSavingRef.current = true;
    saveConfig.mutate({
      journeyId,
      fieldConfig,
      onSuccess: () => {
        setOnBeforeChange(undefined);
        forceHideSidebar();
      },
      onSettled: () => {
        isSavingRef.current = false;
      },
    });
  };

  // Every deliberate close path must clear the guard before hiding — hideSidebar()
  // routes through onBeforeChange, which would re-open the prompt in a loop.
  const closeAndDiscard = () => {
    setOnBeforeChange(undefined);
    forceHideSidebar();
  };

  // useEvent, not a plain closure: the onBeforeChange guard below is registered
  // once on the clean -> dirty transition and would otherwise keep calling that
  // render's handleSave, saving the draft as it looked at the first edit and
  // silently dropping every edit made after it.
  //
  // Deferred by a tick because one dismissal path is the Escape key: the modal
  // is a HeadlessUI Dialog that closes itself on Escape and registers its
  // key listener as soon as it mounts, so opening it synchronously from the
  // keydown handler lets that same keypress continue bubbling into the fresh
  // listener and close it again — Escape would appear to do nothing at all.
  const promptUnsavedChanges = useEvent(() => {
    setTimeout(() => {
      showUnsavedChangesModal({
        isSaveDisabled: hasEmptyBaseField,
        onSave: handleSave,
        onDiscard: closeAndDiscard,
      });
    });
  });

  // Cancel and the header close button prompt when there are pending edits, so
  // a mis-click cannot silently throw away a part-built config. Escape and
  // backdrop click both reach hideRightSidebar() -> handleHideByPosition ->
  // onBeforeChange, so the effect below covers those paths too.
  const handleClose = () => {
    if (!isDirty) {
      closeAndDiscard();
      return;
    }
    promptUnsavedChanges();
  };

  useEffect(() => {
    if (isDirty) {
      setOnBeforeChange(() => {
        promptUnsavedChanges();
        return false;
      });
    } else {
      setOnBeforeChange(undefined);
    }
    return () => setOnBeforeChange(undefined);
    // promptUnsavedChanges is useEvent-stabilised (see above), so adding it
    // here does not change when this effect re-runs — it only satisfies the
    // exhaustive-deps rule honestly instead of blanket-disabling it.
    // setOnBeforeChange itself is not included: its identity is unstable
    // (fresh every render from useSidebarEventHandlers), which would re-run
    // this effect on every render.
  }, [isDirty, promptUnsavedChanges]); // oxlint-disable-line react-hooks/exhaustive-deps -- setOnBeforeChange identity is unstable

  const handleDraftChange = (nextDraft: FieldConfigDraft) => {
    setDraft(nextDraft);
    setHasEdited(true);
  };

  const handleBaseFieldChange = (
    field: (typeof VISIBLE_BASE_FIELD_KEYS)[number],
    nextValue: FieldConfigValue,
  ) => {
    setDraft((prev) => (prev ? { ...prev, base: { ...prev.base, [field]: nextValue } } : prev));
    setHasEdited(true);
  };

  const handleStartJourneyChange = (isOn: boolean) => {
    setDraft((prev) => (prev ? { ...prev, startJourney: isOn } : prev));
    setHasEdited(true);
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('title')}
        subtitle={t('subtitle')}
        subtitleLocation="below"
        subtitleFont="regular12"
        icon={IconGear}
        iconColor="blue100"
        onClose={handleClose}
      />
      <Sidebar.Content>
        {configQuery.isLoading ? (
          <RelativeSpinner />
        ) : configQuery.isError ? (
          <Stack space={2}>
            <Text font="regular14" color="textSecondary" text={t('errorDescription')} />
            <Button variant="secondary" text={t('retry')} onClick={() => configQuery.refetch()} />
          </Stack>
        ) : draft !== null ? (
          <Stack space={4}>
            <JourneyCard
              title={journeyTitle}
              description={journeyDescription}
              countsText={countsText}
            />
            <Text font="regular14" color="textSecondary" text={t('description')} />
            <FieldConfigForm
              attrs={attrs}
              draft={draft}
              variableGroups={variableGroups}
              fileVariableGroups={fileVariableGroups}
              fileMultiVariableGroups={fileMultiVariableGroups}
              fieldMappingId={fieldMappingId}
              onChange={handleDraftChange}
              baseRows={VISIBLE_BASE_FIELD_KEYS.map((key) => ({
                key,
                label: t(BASE_FIELD_LABEL_KEYS[key]),
                attributeType: BASE_FIELD_ATTRIBUTE_TYPES[key],
                error: emptyBaseFields.includes(key)
                  ? t('baseFieldRequired', { label: t(BASE_FIELD_LABEL_KEYS[key]) })
                  : undefined,
                editor:
                  BASE_FIELD_ATTRIBUTE_TYPES[key] === 'email' ? (
                    <EmailField
                      label={t(BASE_FIELD_LABEL_KEYS[key])}
                      hideLabel
                      variableGroups={filterVariableGroupsByTypes(variableGroups, ['email'])}
                      fieldMappingId={fieldMappingId}
                      defaultValue={toMappedEditorValue(draft.base[key], variableGroups)}
                      // EmailField wraps its list counterpart and emits value[0],
                      // which is undefined once the last pill is deleted. Store the
                      // empty-value marker instead: omitting the key entirely lets
                      // seedBaseDefaults restore the default on the next load, which
                      // would silently undo the clear rather than surfacing the
                      // required-field error the panel shows for an empty base field.
                      onChange={(nextValue: ExtendedFieldMappingEditorValue | undefined) =>
                        handleBaseFieldChange(key, nextValue ?? '')
                      }
                    />
                  ) : (
                    <FieldMappingTextEditor
                      label={t(BASE_FIELD_LABEL_KEYS[key])}
                      hideLabel
                      singleLine
                      plaintext
                      variableGroups={variableGroups}
                      fieldMappingId={fieldMappingId}
                      defaultValue={toMappedEditorValue(draft.base[key], variableGroups)}
                      onChange={(nextValue) => handleBaseFieldChange(key, nextValue)}
                    />
                  ),
              }))}
            />
            <Separator />
            <Stack orientation="horizontal" justify="space-between" align="center">
              <Stack space={1}>
                <Text font="semibold14" text={t('startJourneyWhenAdded')} />
                <Text
                  font="regular14"
                  color="textSecondary"
                  text={draft.startJourney ? t('core.yes') : t('core.no')}
                />
              </Stack>
              <Switch
                label={t('startJourneyWhenAdded')}
                labelVisible={false}
                value={draft.startJourney}
                onChange={handleStartJourneyChange}
              />
            </Stack>
          </Stack>
        ) : null}
      </Sidebar.Content>
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button variant="secondary" text={t('core.cancel')} onClick={handleClose} />
          <Button
            variant="primary-blue"
            text={t('core.save')}
            onClick={handleSave}
            disabled={saveConfig.isLoading || draft === null || !isDirty || hasEmptyBaseField}
          />
        </ButtonGroup>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const locale = {
  en: {
    ...journeyListStrings.en,
    title: 'Journeys setup',
    subtitle: 'Configure journey field mappings',
    description:
      'Configure which fields are shown / pre-filled when an applicant is added to this Journey',
    fullName: 'Full name',
    baseFieldRequired: '{label} is always required — add a value or an applicant field',
    startJourneyWhenAdded: 'Start journey when added',
    errorDescription: 'Failed to load journey configuration. Please try again.',
    retry: 'Retry',
  },
  is: {
    ...journeyListStrings.is,
    title: 'Stillingar Journey-ferla',
    subtitle: 'Stilltu hvaða umsóknargögn fara í reiti Journey-ferlisins',
    description:
      'Veldu hvaða reitir birtast og hvaða reitir eru útfylltir fyrirfram þegar umsækjanda er bætt við þetta Journey-ferli.',
    fullName: 'Fullt nafn',
    baseFieldRequired:
      'Reiturinn „{label}“ er alltaf skyldureitur. Sláðu inn gildi eða veldu reit með upplýsingum um umsækjanda.',
    startJourneyWhenAdded: 'Ræsa Journey-ferli þegar umsækjanda er bætt við',
    errorDescription: 'Ekki tókst að sækja stillingar Journey-ferlisins. Reyndu aftur.',
    retry: 'Reyna aftur',
  },
};
